import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, TrendingUp, Activity, Users, Info, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RANGES = [
  { v: "7", label: "Last 7 days" },
  { v: "30", label: "Last 30 days" },
  { v: "90", label: "Last 90 days" },
  { v: "365", label: "Last 12 months" },
];

function fmt(k) {
  if (k.value === null || k.value === undefined) return "—";
  if (k.unit === "R") return `R${Number(k.value).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (k.unit === "%") return `${k.value}%`;
  if (k.unit) return `${k.value} ${k.unit}`;
  return `${k.value}`;
}

function KpiCard({ k }) {
  return (
    <div className={`group relative rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${k.available ? "border-border/60 bg-card" : "border-dashed border-border/70 bg-secondary/30"}`} data-testid={`kpi-${k.key}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">{k.label}</p>
        {k.hint && <span title={k.hint} className="mt-0.5 shrink-0 text-muted-foreground/60"><Info className="h-3.5 w-3.5" /></span>}
      </div>
      <p className={`mt-2 font-heading text-3xl font-extrabold tracking-tight ${k.available ? "text-foreground" : "text-muted-foreground/50"}`} data-testid={`kpi-value-${k.key}`}>{fmt(k)}</p>
      {!k.available && <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-brand-cyan/80">Needs data source</p>}
      {k.available && k.hint && <p className="mt-1 text-[11px] leading-tight text-muted-foreground">{k.hint}</p>}
    </div>
  );
}

function Section({ icon: Icon, title, items }) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-brand-cyan" />
        <h2 className="font-heading text-lg font-bold">{title}</h2>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
        {items.map((k) => <KpiCard key={k.key} k={k} />)}
      </div>
    </div>
  );
}

export default function AdminReports() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/kpis?days=${days}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  const download = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/admin/reports/kpis/export?days=${days}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tiplyme-kpis-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="animate-fade-up" data-testid="admin-reports">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Reports & KPIs</h1>
          <p className="mt-1 text-sm text-muted-foreground">Commercial, operational and customer performance at a glance.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => download("csv")} disabled={!!exporting} data-testid="export-csv-button" className="gap-1.5 rounded-full">
            {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} CSV
          </Button>
          <Button onClick={() => download("pdf")} disabled={!!exporting} data-testid="export-pdf-button" className="gap-1.5 rounded-full bg-brand-navy text-white hover:bg-brand-navy/90">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
          </Button>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-44" data-testid="report-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {data?.totals && (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { l: "Tips paid", v: data.totals.tips_paid },
            { l: "Gross tips", v: `R${Number(data.totals.gross_tips).toLocaleString("en-ZA")}` },
            { l: "Fee revenue", v: `R${Number(data.totals.fee_revenue).toLocaleString("en-ZA")}` },
            { l: "Merchants", v: data.totals.merchants },
            { l: "Workers", v: data.totals.workers },
            { l: "Active workers", v: data.totals.active_workers },
          ].map((t) => (
            <div key={t.l} className="rounded-2xl bg-brand-navy p-4 text-white shadow-lg shadow-brand-navy/20" data-testid={`total-${t.l.replace(/\s/g, "-").toLowerCase()}`}>
              <p className="text-[11px] uppercase tracking-wide text-white/60">{t.l}</p>
              <p className="mt-1 font-heading text-xl font-extrabold">{t.v}</p>
            </div>
          ))}
        </div>
      )}

      {loading && <p className="mt-8 text-sm text-muted-foreground">Loading KPIs…</p>}

      {data && !loading && (
        <>
          <Section icon={TrendingUp} title="Commercial KPIs" items={data.commercial} />
          <Section icon={Activity} title="Operational KPIs" items={data.operational} />
          <Section icon={Users} title="Customer KPIs" items={data.customer} />
          <p className="mt-8 flex items-center gap-1.5 text-xs text-muted-foreground"><BarChart3 className="h-3.5 w-3.5" /> Metrics marked “Needs data source” require inputs TiplyMe doesn’t capture yet (e.g. ad spend, chargebacks, support tickets, scan tracking).</p>
        </>
      )}
    </div>
  );
}
